import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  FONTS,
  LIST_ITEM_SEPARATOR_HEIGHT,
  SCREEN_PADDING,
  SCREEN_PADDING_LARGE,
  TYPOGRAPHY,
} from '@/styles';
import { formatCurrency } from '@/utils/money';
import { ListSection, RecyclerList } from '@/components/recycler-list';
import {
  PAYMENT_HISTORY_DETAIL_LIST_ITEM_HEIGHT,
  PaymentHistoryDetailItemOption,
  PaymentHistoryDetailListItem,
} from './payment-history-detail-list-item';
import { PaymentHistoryDetailListItemFieldsFragment } from '@/types';
import {
  ScrollViewProps,
  StyleProp,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';

interface Props {
  currencyCode: string | null;
  paymentMemo?: string | null;
  paymentTotal: number;
  scrollViewProps?: ScrollViewProps;
  sections: ListSection<PaymentHistoryDetailItemOption>[];
  setSelectedListItem: (
    paymentBatchLineItem: PaymentHistoryDetailListItemFieldsFragment,
  ) => void;
  transactionFee: number | null;
  transactionFeeName: string | null;
}

export const PaymentHistoryDetailList: React.FC<Props> = ({
  currencyCode,
  paymentMemo,
  paymentTotal,
  scrollViewProps,
  sections,
  setSelectedListItem,
  transactionFee,
  transactionFeeName,
}) => {
  const [styles] = useStyles();

  const summaryStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    styles.summaryContainer,
    [styles.summaryContainer, styles.summaryContainerLarge],
  );

  return (
    <RecyclerList
      estimatedItemSize={PAYMENT_HISTORY_DETAIL_LIST_ITEM_HEIGHT}
      ListFooterComponent={
        <View style={summaryStyle}>
          {transactionFee ? (
            <View style={styles.summaryRow}>
              <Text style={TYPOGRAPHY.p}>
                {transactionFeeName
                  ? transactionFeeName
                  : i18n.t('transaction_fee')}
              </Text>
              <Text style={[TYPOGRAPHY.h3, styles.transactionFeeText]}>
                {formatCurrency(transactionFee, currencyCode)}
              </Text>
            </View>
          ) : null}
          <View style={styles.summaryRow}>
            <Text style={styles.totalText}>{i18n.t('total')}</Text>
            <Text style={styles.totalText}>
              {formatCurrency(paymentTotal, currencyCode)}
            </Text>
          </View>
          {paymentMemo ? (
            <View style={styles.memoContainer}>
              <Text style={TYPOGRAPHY.pSemiBold}>{i18n.t('memo')}</Text>
              <Text style={TYPOGRAPHY.p}>{paymentMemo}</Text>
            </View>
          ) : null}
        </View>
      }
      renderItem={({ item }) => (
        <PaymentHistoryDetailListItem
          currencyCode={currencyCode}
          option={item}
          setSelectedListItem={setSelectedListItem}
        />
      )}
      sections={sections}
      testID="payment-history-detail-list"
      {...scrollViewProps}
    />
  );
};

const useStyles = createStyles((theme) => ({
  memoContainer: {
    marginTop: 16,
  },
  summaryContainer: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: LIST_ITEM_SEPARATOR_HEIGHT,
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: 16,
    top: -LIST_ITEM_SEPARATOR_HEIGHT,
  },
  summaryContainerLarge: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  summaryRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  totalText: {
    ...FONTS.primarySemiBold,
    fontSize: 22,
  },
  transactionFeeText: {
    fontSize: 16,
  },
}));
